import { RoutesProvider } from '@folklore/routes';
import { useEffect } from 'react';
import { flushSync } from 'react-dom';
import { IntlProvider } from 'react-intl';
import { type AroundNavHandler, Router } from 'wouter';

import { AppProviders } from '../contexts/AppProviders';
import { QueriesProvider } from '../contexts/QueriesContext';

import type { Page } from '../types/entities';
import Routes from './Routes';

interface Position {
    latitude: number;
    longitude: number;
}

interface MenuItem {
    label: string;
    url: string;
    external?: boolean;
    current?: boolean;
    [key: string]: unknown;
}

interface Menus {
    headerTopMenu?: MenuItem[];
    headerMainMenu?: MenuItem[];
    socialMenu?: MenuItem[];
    footerMenu?: MenuItem[][];
}

interface Size {
    width: number;
    height: number;
}

interface Scroll {
    x: number;
    y: number;
}

interface LayoutState {
    size?: Size;
    scroll?: Scroll;
    fontsLoaded?: boolean;
    menuOpened?: boolean;
    popupClosed?: boolean;
    footerIsPurple?: boolean;
}

interface QuizResults {
    [key: string]: unknown;
}

interface QuizState {
    results?: QuizResults;
    count?: number;
}

interface SiteState {
    pages?: Record<string, Page>;
    popup?: unknown;
    categories?: unknown[];
    menus?: Menus;
    lavalPosition?: Position;
    newArticle?: unknown;
    lastLocation?: unknown;
    statusCode?: number | null;
    showCookieConsent?: boolean;
    animationComplete?: boolean;
}

interface ApiKeys {
    google?: string;
    mapbox?: string;
    [key: string]: string | undefined;
}

interface AppProps {
    intl?: {
        locale?: string;
        messages?: Record<string, Record<string, string>> | Record<string, string>;
    } | null;
    routes?: Record<string, string>;
    site?: SiteState;
    popup?: unknown;
    page?: Page | null;
    layout?: LayoutState;
    quiz?: QuizState;
    homeSplash?: unknown;
    keys?: ApiKeys;
    menus?: Menus;
}

function App({
    intl = null,
    routes = { home: '/' },
    site,
    popup,
    page,
    layout,
    quiz,
    homeSplash,
    keys,
    menus,
    ...rest
}: AppProps) {
    const { locale = 'fr', messages = {} } = intl || {};

    // useEffect(() => {
    //     window.history.scrollRestoration = 'manual';
    // }, []);

    const aroundNav: AroundNavHandler = (navigate, to, options) => {
        if (
            typeof document.startViewTransition === 'undefined' ||
            typeof document.activeViewTransition === 'undefined'
        ) {
            navigate(to, options);
            window.scrollTo(0, 0);
            return;
        }

        // Hidden tabs can't run a view transition: the browser skips it and rejects its promises
        if (!document.activeViewTransition && document.visibilityState === 'visible') {
            const transition = document.startViewTransition(() => {
                // eslint-disable-next-line @eslint-react/dom-no-flush-sync
                flushSync(() => {
                    navigate(to, options);
                    window.scrollTo(0, 0);
                });
            });
            // A skipped transition (e.g. the tab gets hidden mid-way) rejects these promises with a
            // DOMException while the navigation still happens; only errors thrown by the navigation
            // itself are let through, once
            transition.ready.catch(() => {});
            transition.finished.catch(() => {});
            transition.updateCallbackDone.catch((error: unknown) => {
                if (!(error instanceof DOMException)) {
                    throw error;
                }
            });
        } else {
            navigate(to, options);
            window.scrollTo(0, 0);
        }
    };

    // console.log('page', page);
    // console.log('routes', routes);

    return (
        <AppProviders
            site={site}
            popup={popup}
            page={page}
            layout={layout}
            quiz={quiz}
            homeSplash={homeSplash}
            menus={menus}
            keys={keys}
        >
            <IntlProvider locale={locale} messages={messages[locale] || messages || null}>
                <Router aroundNav={aroundNav}>
                    <RoutesProvider routes={routes}>
                        <Routes />
                    </RoutesProvider>
                </Router>
            </IntlProvider>
        </AppProviders>
    );
}

export default App;
